import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, X, Check } from "lucide-react";
import { useClickOutside } from "../lib/hooks";

// Tailwind needs full class strings literally so it can't compose them at runtime.
const VARIANT_STYLES = {
  green: {
    containerFocus: "focus-within:border-green-500",
    chip: "bg-green-50 text-green-700 border-green-100",
    optionSelected: "bg-green-50 text-green-700 border-green-100",
    checkboxSelected: "bg-green-500 border-green-500 shadow-[0_0_10px_rgba(34,197,94,0.3)]",
  },
  amber: {
    containerFocus: "focus-within:border-amber-600",
    chip: "bg-amber-50 text-amber-700 border-amber-100",
    optionSelected: "bg-amber-50 text-amber-700 border-amber-100",
    checkboxSelected: "bg-amber-600 border-amber-600 shadow-[0_0_10px_rgba(217,119,6,0.3)]",
  },
};

export const DataThemesSection = ({
  euDataThemes,
  selected,
  onToggle,
  error,
  variant = "green",
}) => {
  const v = VARIANT_STYLES[variant] || VARIANT_STYLES.green;
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  useClickOutside(containerRef, () => setIsOpen(false));

  const handleRemove = (e, code) => {
    e.stopPropagation(); 
    onToggle(code);
  };

  return (
    <div className="space-y-4 relative" ref={containerRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-start justify-between w-full min-h-[60px] px-4 py-3 border-2 rounded-2xl cursor-pointer bg-white transition-all
          ${error
            ? "border-red-500 ring-4 ring-red-50"
            : `border-slate-100 hover:border-slate-300 ${v.containerFocus} shadow-sm`}
        `}
      >
        <div className="flex flex-wrap gap-2 flex-1">
          {selected.length > 0 ? (
            selected.map((code) => {
              const theme = euDataThemes.find((t) => t.code === code);
              return (
                <motion.span
                  layout
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  key={code}
                  className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-xl text-sm font-bold border shadow-sm ${v.chip}`}
                >
                  {theme?.label || code}
                  <button
                    type="button"
                    onClick={(e) => handleRemove(e, code)}
                    className="hover:text-red-500 transition-colors"
                  >
                    <X size={14} />
                  </button>
                </motion.span>
              );
            })
          ) : (
            <span className="text-slate-400 font-medium py-1.5">Select Europa Data Themes...</span>
          )}
        </div>
        
        <div className="flex items-center self-center pl-2">
          <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </div>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute z-[100] w-full mt-2 bg-white border border-slate-200 rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.12)] overflow-hidden"
          >
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-80 overflow-y-auto custom-scrollbar">
              {euDataThemes.map(({ code, label }) => {
                const isSelected = selected.includes(code);
                return (
                  <div
                    key={code}
                    onClick={() => onToggle(code)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all group
                      ${isSelected
                        ? `border ${v.optionSelected}`
                        : "hover:bg-slate-50 text-slate-600 border border-transparent hover:border-slate-100"}
                    `}
                  >
                    {/* Checkbox */}
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0
                      ${isSelected
                        ? v.checkboxSelected
                        : "border-slate-200 bg-white group-hover:border-slate-400"}`}
                    >
                      {isSelected && <Check size={12} className="text-white stroke-[4]" />}
                    </div>

                    <span className="text-sm font-semibold select-none truncate">
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <motion.p
          initial={{ opacity: 0, x: -5 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-red-500 text-xs font-bold italic mt-2 ml-2"
        >
          {typeof error === "string" ? error : "Selection Required"}
        </motion.p>
      )}
    </div>
  );
};
